import { useCallback, useEffect, useMemo, useState } from "react";
import { useAlert } from "context/AlertContext";
import apiKnowledgeHub from "../api";

// Shared data source for the hub and the chapter page. Chapters are numbered by
// their position in the API order so "CH 03" means the same thing on both pages.
const useKnowledgeHub = (searchText = "") => {
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [chapters, setChapters] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiKnowledgeHub
      .getAll({ searchText: searchText || undefined })
      .then((res) => {
        if (cancelled) return;
        if (res.data.status) {
          setChapters(res.data.data.chapters || []);
        } else {
          showAlert({ type: "error", message: res.data?.message || "Failed to load knowledge base" });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          showAlert({ type: "error", message: error.response?.data?.message || "Failed to load knowledge base" });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // Flat article list, each tagged with its chapter and chapter number.
  const articles = useMemo(
    () =>
      chapters.flatMap((chapter, ci) =>
        (chapter.articles || []).map((article, ai) => ({
          ...article,
          chapter,
          chapterNumber: ci + 1,
          orderInChapter: ai + 1,
        }))
      ),
    [chapters]
  );

  const toggleBookmark = useCallback(
    async (articleId) => {
      const flip = () =>
        setChapters((prev) =>
          prev.map((chapter) => ({
            ...chapter,
            articles: (chapter.articles || []).map((a) =>
              a.id === articleId
                ? { ...a, viewer_state: { ...a.viewer_state, is_bookmarked: !a.viewer_state?.is_bookmarked } }
                : a
            ),
          }))
        );
      flip();
      try {
        const res = await apiKnowledgeHub.toggleBookmark(articleId);
        if (res.data?.status === false) throw new Error(res.data?.message);
      } catch (error) {
        flip();
        showAlert({ type: "error", message: error.response?.data?.message || "Could not update bookmark" });
      }
    },
    [showAlert]
  );

  return { loading, chapters, articles, toggleBookmark };
};

export default useKnowledgeHub;
